import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { WaterDrop } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import {
  SubprojectBuildingDetails,
  SubprojectTechnology,
} from '../../stores/SubprojectStore.ts';
import { DecarbContentLayout } from '../components/DecarbContentLayout.tsx';
import { DecarbOverviewHeader } from '../components/DecarbOverviewHeader.tsx';
import { FilterMenu, SearchBox, SortMenu } from '../components/TechToolbar';
import { useHVACQuery } from '../queries/hvac';
import {
  EnergyKind,
  HVAC,
  Tab,
  TechSortDimensions,
  TechSortState,
} from '../types';
import {
  isPassiveTechnology,
  normalizeToRatingLimit,
  storedCategoryCapitalCost,
} from '../utils.ts';
import { HVACUnit } from './HVACUnit.tsx';
import {
  computeHVACMinMax,
  getAvailableHvacList,
  getEnergyUsageForHvac,
  getHvacCoverageWeight,
  getPossibleUnitsForHvac,
  groupHvacsByType,
} from './hvacUtils';

interface HVACForBuildingDetailsProps {
  subproject: SubprojectBuildingDetails;
  emptyText: React.ReactNode;
  tab: Tab;
  onAddHvac: (hvacId: string, limit: number) => void;
  onChangeHvacCount: (hvacId: string, newCount: number) => void;
  onCopyConvetionalAlternative: (tab: Tab) => void;
  isLoading?: boolean;
  readOnly?: boolean;
  /**
   * True while the local selections have not yet been round-tripped through the
   * API. The passive charge is computed and stored by the backend, so any
   * stored HVAC total is known to predate the current selections while this is
   * set, and the capital cost is shown as the no-value placeholder rather than
   * a figure the next save will replace.
   */
  costsArePending?: boolean;
}

export const HVACForBuildingDetails: React.FC<HVACForBuildingDetailsProps> = ({
  subproject,
  emptyText,
  tab,
  onChangeHvacCount,
  onAddHvac,
  isLoading,
  readOnly,
  costsArePending,
}) => {
  const { areaUnits } = useEnumHelpers();
  const { data: hvacs = [] } = useHVACQuery();
  const hvacUnit = areaUnits.find((v) => v.name === 'sqft')!;
  const buildingAlternative = subproject[tab];
  const [searchString, setSearchString] = useState<string>('');
  const [sortState, setSortState] = useState<TechSortState>({
    dimension: TechSortDimensions[0],
    direction: 'asc',
  });
  const [filter, setFilter] = useState<EnergyKind[]>([]);

  const availableEnergyTypes = useMemo(() => {
    const types = new Set(hvacs.map((h) => h.energySink).filter(Boolean));
    return [...types] as EnergyKind[];
  }, [hvacs]);

  // Calculate buildingUnit early, but handle null case
  const buildingUnit = buildingAlternative?.buildingParams?.conditionedSpace
    ?.unit
    ? areaUnits.find((v) => {
        return (
          v.name.toLowerCase() ===
          buildingAlternative.buildingParams.conditionedSpace.unit.toLowerCase()
        );
      })
    : undefined;

  const minMax = useMemo(() => computeHVACMinMax(hvacs), [hvacs]);

  // Whether any selected row is passive -- drives the header's helper text
  // explaining the 15%-of-design-cost charge. Derived straight from the
  // selections, deliberately outside the unit-dependent reduce below: which
  // technologies are selected does not depend on the conditioned-space unit
  // being known.
  const hasPassiveSelection = useMemo(() => {
    const params =
      buildingAlternative?.technologyParams?.['hvac']?.params ?? [];
    return params.some((tp) => {
      const hvac = hvacs.find((h) => h.id === tp.techId);
      return hvac ? isPassiveTechnology(hvac) : false;
    });
  }, [buildingAlternative, hvacs]);

  const [energyDemand, totalCoverage] = useMemo(() => {
    if (!buildingAlternative?.technologyParams?.['hvac']) {
      return [0, 0];
    }

    const tech = buildingAlternative.technologyParams['hvac'];
    if (!tech) {
      return [0, 0];
    }

    if (!buildingUnit || !hvacUnit) {
      return [0, 0];
    }

    return tech.params.reduce<[number, number]>(
      (v, tp) => {
        const hvac = hvacs.find((h) => h.id === tp.techId);
        if (!hvac) {
          return v;
        }

        const passive = isPassiveTechnology(hvac);

        const energyDemand = getEnergyUsageForHvac(
          hvac,
          subproject.airExchangeFactor,
          tp.units,
        );
        const possibleUnits = getPossibleUnitsForHvac(
          hvac,
          hvacUnit,
          buildingAlternative,
          buildingUnit,
        );
        // Passive technologies have 0% coverage (HVAC is the one category with
        // passive catalog rows today). Otherwise each row counts by the HVAC
        // services it provides (heating 30 / cooling 30 / air handling and
        // ventilation 40 / efficiency 0), uncapped at every level.
        const coverage = passive
          ? 0
          : (tp.units / possibleUnits) * getHvacCoverageWeight(hvac);

        const [e, t] = v;
        return [e + energyDemand, t + coverage];
      },
      [0, 0],
    );
  }, [
    hvacUnit,
    buildingAlternative,
    buildingUnit,
    hvacs,
    subproject.airExchangeFactor,
  ]);

  // The stored HVAC capital cost, as the backend last computed and persisted
  // it -- the ONLY source for this tile, active and passive rows alike. It
  // carries the regional multiplier, each catalog row's own inflation ratio
  // and the passive 15%-of-design-cost charge (calculations/orchestration.py);
  // recomputing any of that here would be a second copy of the cost chain
  // that drifts the next time the chain changes. The tab badge
  // (HVACSelection) reads the same figure, so the two agree by construction.
  // `undefined` means "no stored figure to show" and renders the no-value
  // placeholder rather than a number we know the backend will replace.
  // `costsArePending` is the dirty flag: the owning page knows whether the
  // current selections have been round-tripped through the API yet. The rule
  // itself is the shared one every category screen uses.
  const capitalCost = storedCategoryCapitalCost(
    buildingAlternative,
    'hvac',
    Boolean(costsArePending),
  );

  const selectedHvacSystems: SubprojectTechnology | undefined =
    buildingAlternative.technologyParams['hvac'];

  const availableHvacList = useMemo(
    () =>
      getAvailableHvacList(
        hvacs,
        selectedHvacSystems?.params ?? [],
        tab,
        searchString,
        filter,
        sortState,
        minMax,
      ),
    [hvacs, selectedHvacSystems, searchString, sortState, filter, tab, minMax],
  );

  // Handle case where buildingAlternative is null or missing buildingParams
  if (!buildingAlternative || !buildingAlternative.buildingParams) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant='h6' color='text.secondary'>
          Building parameters not yet configured. Please complete the Space
          Usage step first.
        </Typography>
      </Box>
    );
  }

  // Handle loading state
  if (hvacs.length === 0 || !hvacUnit || !buildingUnit) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant='h6' color='text.primary'>
          Loading HVAC data...
        </Typography>
      </Box>
    );
  }

  // A multi-type HVAC renders once in each of its category groups. Each group
  // is its own children array in DecarbContentLayout, so keys only need to be
  // unique within a group; the category prefix just makes them self-describing.
  const renderAvailableHvac = (hvac: HVAC, key: string) => (
    <HVACUnit
      key={key}
      onAddHvac={onAddHvac}
      onChangeHvacCount={onChangeHvacCount}
      normalizedCapital={normalizeToRatingLimit(
        minMax?.capitalCostPerUnit.min ?? 0,
        minMax?.capitalCostPerUnit.max ?? 0,
        hvac.capitalCostPerUnit,
        hvac.unitsNeededPerSqft,
      )}
      normalizedEnergy={normalizeToRatingLimit(
        minMax?.energyUsePerYr?.min ?? 0,
        minMax?.energyUsePerYr?.max ?? 0,
        hvac.energyUsePerYr,
        hvac.unitsNeededPerSqft,
      )}
      hvac={hvac}
      limit={getPossibleUnitsForHvac(
        hvac,
        hvacUnit,
        buildingAlternative,
        buildingUnit,
      )}
      totalCount={0}
    />
  );

  return (
    <DecarbContentLayout
      availableGroups={
        readOnly
          ? []
          : groupHvacsByType(availableHvacList).map((group) => ({
              key: group.category,
              label: group.label,
              items: group.items.map((hvac) =>
                renderAvailableHvac(hvac, `${group.category}:${hvac.id}`),
              ),
            }))
      }
      selectedItems={
        selectedHvacSystems?.params.map((tech) => {
          const hvac = hvacs.find((h) => h.id === tech.techId);
          if (!hvac) {
            return <React.Fragment key={tech.techId} />;
          }

          const unitsRequired = getPossibleUnitsForHvac(
            hvac,
            hvacUnit,
            buildingAlternative,
            buildingUnit,
          );

          return (
            <HVACUnit
              key={hvac.id}
              hvac={hvac}
              onAddHvac={onAddHvac}
              onChangeHvacCount={onChangeHvacCount}
              totalCount={tech.units}
              normalizedCapital={normalizeToRatingLimit(
                minMax?.capitalCostPerUnit.min ?? 0,
                minMax?.capitalCostPerUnit.max ?? 0,
                Number(hvac.capitalCostPerUnit),
                Number(hvac.unitsNeededPerSqft),
              )}
              normalizedEnergy={normalizeToRatingLimit(
                minMax?.energyUsePerYr?.min ?? 0,
                minMax?.energyUsePerYr?.max ?? 0,
                Number(hvac.energyUsePerYr),
                Number(hvac.unitsNeededPerSqft),
              )}
              limit={unitsRequired}
              inEdit
              isLoading={isLoading}
              readOnly={readOnly}
            />
          );
        }) ?? []
      }
      overviewHeader={
        <DecarbOverviewHeader
          systemCoverageName={'HVAC'}
          systemCoverageIcon={<WaterDrop />}
          systemCoverageHelperText={"Choose enough HVAC's to get 100% coverage"}
          energyDemand={energyDemand}
          capitalCost={capitalCost}
          capitalCostHelperText={
            hasPassiveSelection
              ? 'Includes a passive measure, charged by the backend at 15% of the building design cost (set on Space Usage). The total is computed on save and refreshes here a moment after each change.'
              : undefined
          }
          coveragePercentage={totalCoverage}
        />
      }
      searchComponent={
        readOnly ? undefined : (
          <SearchBox query={searchString} setQuery={setSearchString} />
        )
      }
      filterComponent={
        readOnly ? undefined : availableEnergyTypes.length > 1 ? (
          <FilterMenu
            listOfAllOptions={availableEnergyTypes}
            selectedOptions={filter}
            onReset={() => setFilter([])}
            onSelect={(option: EnergyKind) =>
              setFilter((prev) => {
                if (prev.includes(option)) {
                  return [...prev.filter((val) => val !== option)];
                } else {
                  return [...prev, option];
                }
              })
            }
          />
        ) : undefined
      }
      sortComponent={
        readOnly ? undefined : (
          <SortMenu
            dimensions={TechSortDimensions}
            activeSort={sortState}
            onSortChange={setSortState}
          />
        )
      }
      helperText={emptyText}
    />
  );
};
